import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { LocationId, ReportSchedule, RmProfileLookupId } from '@brightlocal/shared';

import { prefixKey } from 'shared/utils';

const RM_REPORT_SETUP_STORAGE_KEY = 'rm_report_setups';

/** An RM report set-up whose profile lookup has started but whose report is not created yet. */
export interface PendingRMReportSetup {
  /** Location the report is for. */
  locationId: LocationId;
  /** Profile lookup the report's directories come from. */
  lookupId: RmProfileLookupId;
  /** Schedule chosen when the set-up started. */
  schedule: ReportSchedule;
  /** Day of the month a monthly report runs on; absent for an adhoc one. */
  runOn?: number;
}

interface RMReportSetupStore {
  /** Set-ups waiting for their report, by location. */
  pendingSetupsByLocation: Partial<Record<LocationId, PendingRMReportSetup>>;
  /** Remembers a set-up so its report can still be created after a reload. */
  savePendingSetup: (setup: PendingRMReportSetup) => void;
  /** Forgets a set-up, unless a newer one has replaced it for the same location since. */
  removePendingSetup: (setup: Pick<PendingRMReportSetup, 'locationId' | 'lookupId'>) => void;
}

// TODO: RM v2 can't find a location's profile lookups, so the lookup id is kept in the browser until it can
// [https://brightlocal.atlassian.net/browse/BI-5075]
export const useRMReportSetupStore = create<RMReportSetupStore>()(
  persist(
    (set) => ({
      pendingSetupsByLocation: {},
      savePendingSetup: (setup) =>
        set(({ pendingSetupsByLocation }) => ({
          pendingSetupsByLocation: { ...pendingSetupsByLocation, [setup.locationId]: setup }
        })),
      removePendingSetup: ({ locationId, lookupId }) =>
        set(({ pendingSetupsByLocation }) => {
          const { [locationId]: saved, ...rest } = pendingSetupsByLocation;
          const isSameSetup = saved?.lookupId === lookupId;

          return isSameSetup ? { pendingSetupsByLocation: rest } : { pendingSetupsByLocation };
        })
    }),
    { name: prefixKey(RM_REPORT_SETUP_STORAGE_KEY) }
  )
);
